import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FF_BLOCK_FREE_USER_MODELS, FF_BLOCK_FREE_USER_VIDEO_MODEL, FF_NANO_BANANA_21 } from '@/consts/featureFlags';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useFetchMenuItems } from '@/hooks/useFetchMenuItems';
import { useNodeFiltersStore } from '@/state/nodes/nodes.state';
import { flattenMenuData } from '@/state/nodes/nodes.utils';
import { useUserStore } from '@/state/user.state';
import { SubscriptionType } from '@/types/shared';
import {
  canUseAutoDuration,
  canUseAutoQuality,
  isModelVariantAllowed,
  type ModelVariantFlags,
} from '@/utils/modelSettingRules';
import { DEFAULT_GENERATOR_MODEL_SLUGS } from '@/consts/generatorModelSlugs';
import type { GenerateMode, GenerateModel, GenerateParam } from '@/api/generate.api';

/** `locked` shows in the menu but can't be picked, as on the free plan. */
export type GenerateModelAccess = 'available' | 'locked' | 'hidden';

/** Whether the canvas model menu, which decides what this user may use, has loaded. */
export type GenerateModelAccessStatus = 'loading' | 'ready' | 'failed';

export type GenerateModelAccessContext = {
  subscriptionType: SubscriptionType | undefined;
  hasActiveWeaveMcpPromoCredits?: boolean;
  flags: ModelVariantFlags & { isBlockFreeUserVideoModelEnabled: boolean; isBlockFreeUserModelsEnabled: boolean };
  /**
   * Provider names in the canvas model menu, which the server already filtered for this user: the admin's
   * allow-list, unverified models, rollout flags. Null for a signed-out visitor, who loads no menu.
   */
  menuModelNames: ReadonlySet<string> | null;
};

export function generateModelAccess(
  model: GenerateModel,
  { subscriptionType, hasActiveWeaveMcpPromoCredits, flags, menuModelNames }: GenerateModelAccessContext,
): GenerateModelAccess {
  if (!isModelVariantAllowed(model.modelName, model.variant, flags)) {
    return 'hidden';
  }
  if (menuModelNames && !menuModelNames.has(model.modelName)) {
    return 'hidden';
  }
  const isFreePlan = subscriptionType === SubscriptionType.Free;
  const isPaidOnly =
    (model.mode === 'video' && flags.isBlockFreeUserVideoModelEnabled && !hasActiveWeaveMcpPromoCredits) ||
    (model.freePlanBlocked && flags.isBlockFreeUserModelsEnabled);
  return isFreePlan && isPaidOnly ? 'locked' : 'available';
}

/**
 * `model` with only the setting options the plan allows: no Auto quality on GPT Image for a free workspace. The
 * options are part of the settings fingerprint, so an Auto saved before a downgrade is dropped.
 */
export function withPlanSettings(model: GenerateModel, subscriptionType: SubscriptionType | undefined): GenerateModel {
  if (canUseAutoQuality(model.modelName, subscriptionType)) {
    return model;
  }
  const withoutAutoQuality = (param: GenerateParam): GenerateParam => {
    if (param.key !== 'quality' || param.type !== 'enum') {
      return param;
    }
    const options = param.options.filter((option) => option !== 'auto');
    return { ...param, options, defaultValue: options.includes(param.defaultValue) ? param.defaultValue : options[0] };
  };
  return {
    ...model,
    params: model.params.map(withoutAutoQuality),
    moreParams: model.moreParams.map(withoutAutoQuality),
  };
}

/** The mode's default model when `models` has it, else the first model of that mode. `models` holds the ones the user can pick. */
export function defaultModelFor(models: readonly GenerateModel[], mode: GenerateMode): GenerateModel | undefined {
  const ofMode = models.filter((model) => model.mode === mode);
  return ofMode.find((model) => model.slug === DEFAULT_GENERATOR_MODEL_SLUGS[mode]) ?? ofMode[0];
}

/** Loads the canvas model menu once for a signed-in visitor, and reads the plan and flags the rules need. */
export function useGenerateModelAccess(signedIn: boolean) {
  const subscriptionType = useUserStore((state) => state.user?.activeWorkspace?.subscription?.type);
  const hasActiveWeaveMcpPromoCredits = useUserStore(
    (state) => state.user?.activeWorkspace?.hasActiveWeaveMcpPromoCredits === true,
  );
  const { value: isNanoBanana21Enabled } = useFeatureFlag(FF_NANO_BANANA_21);
  const { value: isBlockFreeUserVideoModelEnabled } = useFeatureFlag(FF_BLOCK_FREE_USER_VIDEO_MODEL);
  const { value: isBlockFreeUserModelsEnabled } = useFeatureFlag(FF_BLOCK_FREE_USER_MODELS);
  const menu = useNodeFiltersStore((state) => state.menu);
  const isMenuDataLoaded = useNodeFiltersStore((state) => state.isMenuDataLoaded);
  const { fetchMenuItems } = useFetchMenuItems();
  const requestedMenu = useRef(false);
  const [menuRequest, setMenuRequest] = useState<'idle' | 'pending' | 'done'>('idle');
  const menuItems = useMemo(() => flattenMenuData(menu), [menu]);
  const menuNames = useMemo(() => menuItems.map((item) => item.name), [menuItems]);
  // the same backoffice field the canvas's left panel reads for its New tag
  const newModelNames = useMemo(
    () => new Set(menuItems.filter((item) => item.isNew).map((item) => item.name)),
    [menuItems],
  );
  // the store marks the menu loaded after a failure too, which leaves it empty, so only real items count
  const hasMenu = isMenuDataLoaded && menuNames.length > 0;

  useEffect(() => {
    if (!signedIn || hasMenu || requestedMenu.current) {
      return;
    }
    requestedMenu.current = true;
    setMenuRequest('pending');
    void fetchMenuItems().then(() => setMenuRequest('done'));
  }, [fetchMenuItems, hasMenu, signedIn]);

  // nothing is hidden until a menu is in
  const menuModelNames = useMemo(
    () => (signedIn && hasMenu ? new Set(menuNames) : null),
    [hasMenu, menuNames, signedIn],
  );

  const accessOf = useCallback(
    (model: GenerateModel) =>
      generateModelAccess(model, {
        subscriptionType,
        hasActiveWeaveMcpPromoCredits,
        flags: {
          isNanoBanana21Enabled: Boolean(isNanoBanana21Enabled),
          isBlockFreeUserVideoModelEnabled: Boolean(isBlockFreeUserVideoModelEnabled),
          isBlockFreeUserModelsEnabled: Boolean(isBlockFreeUserModelsEnabled),
        },
        menuModelNames,
      }),
    [
      hasActiveWeaveMcpPromoCredits,
      isBlockFreeUserModelsEnabled,
      isBlockFreeUserVideoModelEnabled,
      isNanoBanana21Enabled,
      menuModelNames,
      subscriptionType,
    ],
  );
  const canUseAuto = useCallback(
    (model: GenerateModel) =>
      // the composer turns Auto off itself while a frame is attached, since it knows what is attached
      canUseAutoDuration({ modelName: model.modelName, variant: model.variant, subscriptionType, hasKeyframe: false }),
    [subscriptionType],
  );
  const applyPlanSettings = useCallback(
    (model: GenerateModel) => withPlanSettings(model, subscriptionType),
    [subscriptionType],
  );

  // a model the admin blocked shows until the menu loads, and some flags are checked only there, so the
  // page must not run anything before it loads or after it fails
  const status: GenerateModelAccessStatus =
    !signedIn || hasMenu ? 'ready' : menuRequest === 'done' ? 'failed' : 'loading';

  const isNew = useCallback((model: GenerateModel) => newModelNames.has(model.modelName), [newModelNames]);

  return { accessOf, canUseAutoDuration: canUseAuto, withPlanSettings: applyPlanSettings, isNew, status };
}
